import { Stack, mergeStyleSets, Text, DefaultButton } from "@fluentui/react";
import React from "react";
import { commonStyles } from "../CommonStyles/CommonStyles";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from '../../utilities/AppInsights';
import appInsights, { generateCookieId } from "../../utilities/AppInsights";

const Skills: React.FC = () => {
    const images = (require as any).context("../../assets", true);
    const skillsimg = images("./skills.png");
    const skillspeople = images("./skillpeople.png");
    const { t, i18n } = useTranslation();

    const styles = mergeStyleSets({
        skillsContainer: {
            backgroundColor: "#fff",
            padding: "50px 0px",
            "@media (max-width: 768px)": {
                marginTop: "0px",
            },
            "@media (max-width: 1024px)": {
                padding: "48px 0px",
            },
        },
        link: {
            color: "#0074D3", textDecoration: "none", fontSize: "16px"
        },
        skillHeading: {
            fontSize: "32px",
            fontWeight: 600,
            lineHeight: "40px",
            textAlign: "left"

        },
        skillDescription: {
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "21.28px",
            textAlign: "left",
            marginTop: "20px",
            "@media (min-width:769px) and (max-width: 940px)": {
                marginTop: "10px",
            },
        },
        details: {
            display: "flex", // Ensure content and image are in a flex layout
            flexDirection: "row", // Horizontal layout
            alignItems: "center",
            border: "1px solid #E6F2FB",
            boxShadow: "0px 2px 4px 0px #00000024",
            backgroundColor: "white",
            padding: "10px",
            borderRadius: "24px",
            boxSizing: "border-box", // Include padding and border in width calculations
            width: "100%", 
            "@media (max-width: 768px)": {
                marginBottom:"20px",
                display: "block !important",              
            },
            "@media (min-width: 769px)": {
                display: "flex"
            },
        },
        skillsButton: {
            height: "32px",
            border: "none",
            backgroundColor: "#0078D4",
            color: "white",
            borderRadius: "8px",
            marginTop: "20px",
            padding: "20px",
            "& span": {
                fontSize: "15px", // add font-size for span
            },
            "@media (min-width:769px) and (max-width: 940px)": {
                marginTop: "10px",
            },
            ":focus::after": {
                borderRadius: "8px !important",
                border: "2px solid black !important"
            },
        },
        skillImg: {
            "@media (max-width: 768px)": {
                display: "none"
            },
            width: "272px", 
            height: "318px",
            objectFit:"cover",
            borderRadius: "16px", 
            "@media (max-width: 1400px)": {
                width: "272px !important"
            },
    
            "@media (min-width: 769px)": {
                display: "flex"
            },
            "@media (min-width:769px) and (max-width: 940px)": {
                width: "272px !important",
                padding: "0rem 0rem 0rem 0rem",
            },
        },

        skillImgMob: {
            objectFit: "cover",
            borderRadius: "16px",
            "@media (max-width: 768px)": {
                display: "flex",
                height: "437px !important",
            },
            "@media (min-width: 769px)": {
                display: "none"
            },
            "@media (min-width:769px) and (max-width: 940px)": {
                width: "350px !important",
                padding: "0rem 0rem 0rem 0rem",
            },
        },
        detailsDiv: {
            padding:"0 20px",
            flex: 1,
            "@media (max-width: 768px)": {
                padding: "20px 20px",
            },
        },
        cardContainer: {
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: "20px", 
            width: "100%",
            boxSizing: "border-box", 
            "@media (min-width: 1400px)": {
                gridTemplateColumns: "repeat(2, 1fr)", 
            },
            "@media (max-width: 768px)": {
                display: "block !important"
            },
        
        },
        card: {
            flex: "1 1 calc(50% - 20px)", // Two cards per row with spacing
            maxWidth: "calc(50% - 20px)", // Ensure proper width
            boxSizing: "border-box",
            marginBottom: "20px", // Add spacing between rows
            "@media (max-width: 768px)": {
                flex: "1 1 100%", // Full width for smaller screens
                maxWidth: "100%",
            },
        },
        singleCard: {
            flex: "1 1 100%", // Full width for one card
            maxWidth: "100%",
            boxSizing: "border-box",
            marginBottom: "20px",

        },
        singleCardImg: {
               objectFit:"cover",
            borderRadius: "16px",
            width: "auto", 
            height: "auto",
            "@media (max-width: 1400px)": {
                width: "500px !important"
            },
            "@media (min-width:769px) and (max-width: 940px)": {
                width: "350px !important",
                padding: "0rem 0rem 0rem 0rem",
            },
            "@media (max-width: 768px)": {
              display:"none"
            },
        },

    });

    // Fetch skills array from translations
    const skills = t('Skills', { returnObjects: true }) as Array<{
        heading: string;
        description: string;
        skillsLink: string;
        assessment: string;
        imageUrl: string;
    }>;

    // Filter out invalid or empty skills
    const validSkills = skills.filter(skill =>
        skill.heading?.trim() &&
        skill.description?.trim() &&
        skill.skillsLink?.trim() &&
        skill.assessment?.trim()
    );

    // Display nothing if there are no valid skills
    if (validSkills.length === 0) {
        return null;
    }
    return (
        <Stack className={styles.skillsContainer} style={{
            background: ` url(${skillsimg}) no-repeat center `,
                backgroundSize: "cover",
        }}>
            <Stack className={commonStyles.container}>

                {validSkills?.length > 0 && (
                    <Stack className={validSkills.length === 1 ? styles.singleCard : styles.cardContainer}>
                        {validSkills.map((skill, index) => (
                            <Stack key={index} className={
               styles.details
                            }>
                                <Stack>
                                    <img src={skill.imageUrl} alt="skills" className={styles.skillImgMob} />
                                </Stack>
                                <Stack className={styles.detailsDiv}>
                                    <Text variant="large" className={styles.skillHeading}>
                                        {skill.heading}
                                    </Text>
                                    <Text as="h2" variant="medium" className={styles.skillDescription}>
                                        {skill.description}
                                    </Text>
                                    <Link
                                        to={skill.skillsLink}
                                        target="_blank"
                                        tabIndex={-1}
                                        onClick={() => {
                                            trackEvent("SkillsAssessmentButtonClick", {
                                                ButtonText: skill.assessment,
                                                ExternalLink: skill.skillsLink,
                                                visitorId: generateCookieId(),
                                            });
                                        }}
                                    >
                                        <DefaultButton className={styles.skillsButton}>
                                            {skill.assessment}
                                        </DefaultButton>
                                    </Link>
                                </Stack>
                                <Stack>
                                    <img
                                        src={skill.imageUrl} 
                                        alt="skills"
                                        className={validSkills.length === 1 ? styles.singleCardImg : styles.skillImg}
                                    />
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Stack> 
    );

};

export default Skills;
